import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to check if a feature flag is enabled.
 * 
 * @param {string} flagKey - The feature flag key to check
 * @param {Object} context - Optional context for targeted rollout
 * @param {string} context.school_id - School ID for B2B targeting
 * @param {string} context.user_id - User ID for user targeting
 * @param {string} context.phone_number - Phone number for targeting
 * @param {string} context.role - User role (admin, teacher, parent, student)
 * @param {string} context.subscriber_id - Subscriber ID for targeting
 * @param {string} context.student_id - Student ID for targeting
 * @returns {Object} { isEnabled, isLoading, error, refetch }
 */
export function useFeatureFlag(flagKey, context = {}) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkFlag = useCallback(async () => {
    if (!flagKey) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/admin/feature-flags/check/${flagKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(context),
      });

      if (!response.ok) {
        throw new Error('Failed to check feature flag');
      }

      const data = await response.json();
      setIsEnabled(data.enabled);
    } catch (err) {
      console.error(`Error checking feature flag ${flagKey}:`, err);
      setError(err.message);
      // Default to disabled on error
      setIsEnabled(false);
    } finally {
      setIsLoading(false);
    }
  }, [flagKey, JSON.stringify(context)]);

  useEffect(() => {
    checkFlag();
  }, [checkFlag]);

  return { isEnabled, isLoading, error, refetch: checkFlag };
}

/**
 * Hook to get multiple feature flags at once.
 * 
 * @param {string[]} flagKeys - Array of feature flag keys to check
 * @param {Object} context - Optional context for targeted rollout
 * @returns {Object} { flags: { [key]: boolean }, isLoading, error }
 */
export function useFeatureFlags(flagKeys, context = {}) {
  const [flags, setFlags] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkFlags = useCallback(async () => {
    if (!flagKeys || flagKeys.length === 0) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check all flags in parallel
      const results = await Promise.all(
        flagKeys.map(async (flagKey) => {
          try {
            const response = await fetch(`${API_URL}/api/admin/feature-flags/check/${flagKey}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(context),
            });

            if (!response.ok) {
              return { key: flagKey, enabled: false };
            }

            const data = await response.json();
            return { key: flagKey, enabled: data.enabled };
          } catch {
            return { key: flagKey, enabled: false };
          }
        })
      );

      const flagsMap = {};
      results.forEach(({ key, enabled }) => {
        flagsMap[key] = enabled;
      });

      setFlags(flagsMap);
    } catch (err) {
      console.error('Error checking feature flags:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [JSON.stringify(flagKeys), JSON.stringify(context)]);

  useEffect(() => {
    checkFlags();
  }, [checkFlags]);

  return { flags, isLoading, error, refetch: checkFlags };
}

/**
 * Component wrapper that renders children only if feature is enabled.
 * 
 * Usage:
 * <FeatureFlag flag="b2b_teacher_chat" context={{ school_id: "123" }}>
 *   <ChatComponent />
 * </FeatureFlag>
 */
export function FeatureFlag({ flag, context = {}, children, fallback = null }) {
  const { isEnabled, isLoading } = useFeatureFlag(flag, context);

  if (isLoading) {
    return fallback;
  }

  return isEnabled ? children : fallback;
}

export default useFeatureFlag;
